import React, { useState, useEffect } from 'react'
import { Box, Button, Grid, IconButton, Checkbox, FormControlLabel, Alert, Typography, Paper } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { isEqual } from 'lodash'
import { useGlobalNavigationBlock } from './useGlobalNavigationBlock'
import GenericTextField from 'components/UserFields/GenericTextField'
import GenericDatePicker from 'components/UserFields/GenericDatePicker'
import GenericMultiSelectCheckboxField from 'components/UserFields/GenericMultiSelectCheckboxField'
import GenericSelectField from 'components/UserFields/GenericSelectField'
import {
  getVisaOptions,
  getLanguageOptions,
  getProficiencyOptions,
} from 'components/UserFields/helpers'
import {
  getChangedFields,
  clearFieldError,
  handleInputChangeHelper,
} from '../helpers'

const BackgroundInformationSection = ({ formData, onSave }) => {
  const [localFormData, setLocalFormData] = useState(formData)
  const [initialData, setInitialData] = useState(formData)
  const [hasChanges, setHasChanges] = useState(false)
  const [errors, setErrors] = useState({})
  const [requiresVisa, setRequiresVisa] = useState(
    Array.isArray(localFormData.visaIds) && localFormData.visaIds.length > 0,
  )
  const [candidateLanguages, setLanguages] = useState(
    localFormData.candidateLanguages || [{ language: '', proficiency: '' }],
  )
  useGlobalNavigationBlock(hasChanges)

  useEffect(() => {
    setLocalFormData(formData)
    setInitialData(formData)
    setHasChanges(false)
    setLanguages(
      formData.candidateLanguages?.map(lang => ({
        language: lang.language,
        proficiency: lang.proficiency,
      })) || [],
    )
    if (formData.visaIds && formData.visaIds.length > 0) {
      setRequiresVisa(true)
    }
  }, [formData])

  useEffect(() => {
    const localFormDataCopy = { ...localFormData }
    const initialDataCopy = { ...initialData }

    delete localFormDataCopy.aboutRaw
    delete initialDataCopy.aboutRaw

    setHasChanges(!isEqual(localFormDataCopy, initialDataCopy))
  }, [localFormData, initialData])

  const handleInputChange = event => {
    const { name } = event.target
    clearFieldError(name, setErrors)
    handleInputChangeHelper(event, setLocalFormData)
  }

  const handleVisaCheckboxChange = event => {
    const checked = event.target.checked
    setRequiresVisa(checked)
    setLocalFormData(prev => ({
      ...prev,
      visaIds: checked ? prev.visaIds || [] : [],
    }))
    setHasChanges(true)
  }

  const handleVisaIdsChange = event => {
    const selectedVisaIds = event.target.value
    const updatedVisas = getVisaOptions().filter(option => selectedVisaIds.includes(option.id))
    setLocalFormData(prev => ({ ...prev, visaIds: updatedVisas }))
    setHasChanges(true)
  }

  const handleLanguageChange = (index, field, value) => {
    const updatedLanguages = candidateLanguages.map((lang, i) =>
      i === index ? { ...lang, [field]: value } : lang,
    )
    setLanguages(updatedLanguages)
    setLocalFormData(prev => ({ ...prev, candidateLanguages: updatedLanguages }))
  }

  const handleAddLanguage = () => {
    const updatedLanguages = [...candidateLanguages, { language: '', proficiency: '' }]
    setLanguages(updatedLanguages)
    setLocalFormData(prev => ({ ...prev, candidateLanguages: updatedLanguages }))
  }

  const handleRemoveLanguage = index => {
    const updatedLanguages = candidateLanguages.filter((_, i) => i !== index)
    setLanguages(updatedLanguages)
    setLocalFormData(prev => ({ ...prev, candidateLanguages: updatedLanguages }))
  }

  const handleSave = () => {
    const changedFields = getChangedFields(localFormData, initialData)
    if ('visaIds' in changedFields) {
      changedFields['visaIds'] = localFormData.visaIds.map(v => v.id)
    }
    if ('candidateLanguages' in changedFields) {
      changedFields['candidateLanguages'] = localFormData.candidateLanguages.filter(
        lang => lang.language && lang.proficiency,
      )
    }
    if (Object.keys(changedFields).length > 0) {
      onSave({ ...changedFields, id: formData.id })
      setInitialData(localFormData)
      setHasChanges(false)
    }
  }

  return (
    <Box>
      {hasChanges && (
        <Alert 
          severity='info' 
          sx={{ 
            mb: 3,
            borderRadius: 2,
            '& .MuiAlert-icon': {
              color: 'info.main'
            }
          }}
        >
          You have unsaved changes in this section. Please save before navigating away.
        </Alert>
      )}

      <Grid container spacing={4}>
        {/* Single consolidated background information section */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.default'
            }}
          >
            {/* Residency Information */}
            <Typography variant="subtitle1" sx={{ mb: 3, fontWeight: 500 }}>
              Residency Information
            </Typography>
            <Grid container spacing={3} sx={{ mb: 4 }}>
              <Grid item xs={12} md={6}>
                <GenericTextField
                  name='residency'
                  label='Residency Program'
                  value={localFormData.residency}
                  handleChange={handleInputChange}
                  error={errors.residency}
                  helperText={errors.residency}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'background.paper'
                    }
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <GenericDatePicker
                  name='residencyEnd'
                  label='Residency End Date'
                  value={localFormData.residencyEnd}
                  handleChange={handleInputChange}
                  error={errors.residencyEnd}
                  helperText={errors.residencyEnd}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'background.paper'
                    }
                  }}
                />
              </Grid>
            </Grid>

            {/* Visa Information */}
            <Typography variant="subtitle1" sx={{ mb: 3, mt: 4, fontWeight: 500 }}>
              Visa Information
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={requiresVisa}
                  onChange={handleVisaCheckboxChange}
                  color='primary'
                />
              }
              label='I will require a Visa'
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.9375rem'
                }
              }}
            />
            {requiresVisa && (
              <Box sx={{ mt: 2, mb: 4 }}>
                <GenericMultiSelectCheckboxField
                  value={localFormData.visaIds?.map(v => v.id) || []}
                  label=''
                  name='visaIds'
                  handleChange={handleVisaIdsChange}
                  options={getVisaOptions()}
                  required={requiresVisa}
                  error={errors.visaIds}
                  helperText={errors.visaIds && 'Visa type is required if Visa is needed'}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'background.paper'
                    }
                  }}
                />
              </Box>
            )}

            {/* Languages */}
            <Typography variant="subtitle1" sx={{ mb: 3, mt: 4, fontWeight: 500 }}>
              Languages
            </Typography>
            {candidateLanguages.map((language, index) => (
              <Box 
                key={index} 
                sx={{ 
                  mb: 3,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <Grid container spacing={2} alignItems='center'>
                  <Grid item xs={12} md={5}>
                    <GenericSelectField
                      options={getLanguageOptions()}
                      value={language.language}
                      label='Language'
                      handleChange={e => handleLanguageChange(index, 'language', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: 'background.default'
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={5}>
                    <GenericSelectField
                      options={getProficiencyOptions()}
                      value={language.proficiency}
                      label='Proficiency'
                      handleChange={e => handleLanguageChange(index, 'proficiency', e.target.value)}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: 'background.default'
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <IconButton 
                      onClick={() => handleRemoveLanguage(index)} 
                      color='error'
                      sx={{
                        '&:hover': {
                          bgcolor: 'error.lighter'
                        }
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Box>
            ))}
            <Button 
              onClick={handleAddLanguage} 
              variant='outlined'
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                px: 3,
                py: 1
              }}
            >
              Add a Language
            </Button>
          </Paper>
        </Grid>
      </Grid>

      <Button
        variant='contained'
        color='primary'
        onClick={handleSave}
        disabled={!hasChanges || Object.keys(errors).length > 0}
        sx={{ 
          mt: 4,
          px: 4,
          py: 1.5,
          borderRadius: 2,
          textTransform: 'none',
          fontSize: '0.9375rem',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none'
          }
        }}
      >
        Save Changes
      </Button>
    </Box>
  )
}

export default BackgroundInformationSection
